import clsx from "clsx";
import { type JSX, useEffect, useState } from "react";

import { FileAttachments, UploadAttachmentButton } from "features/attachments";
import Toolbar from "features/templates/components/TemplateEditor/Toolbar";
import useTemplateEditorAttachments from "features/templates/hooks/useTemplateEditorAttachments";
import useTemplateMessageEditor from "features/templates/hooks/useTemplateMessageEditor";
import useTemplateSubjectEditor from "features/templates/hooks/useTemplateSubjectEditor";
import type {
  TemplateSchemaType,
  VoiceTemplateSchemaType,
} from "features/templates/schemas";

import RenderIf from "common/components/RenderIf";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import TemplateAttachment from "../TemplateAttachment";
import TemplateMessageEditor from "./TemplateMessageEditor";
import TemplateSubjectEditor from "./TemplateSubjectEditor";

import type { UseFormReturn } from "react-hook-form";

interface TemplateEditorProps {
  form: UseFormReturn<Exclude<TemplateSchemaType, VoiceTemplateSchemaType>>;
  isEditable: boolean;
  placeholderText?: string;
  customVariables?: { name: string; value: string }[];
  autoFocus?: boolean;
  languageDropdownComponent?: JSX.Element;
}

export default function TemplateEditor({
  form,
  isEditable,
  placeholderText = null,
  customVariables = [],
  autoFocus = false,
  languageDropdownComponent = null,
}: TemplateEditorProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [lastFocusedField, setLastFocusedField] = useState<
    "message" | "subject"
  >("message");

  const {
    getValues,
    formState: { errors },
  } = form;

  const { templateMessageEditor } = useTemplateMessageEditor({
    isEditable,
    customVariables,
    form,
    onFocus: () => {
      setLastFocusedField("message");
    },
  });
  const { templateSubjectEditor } = useTemplateSubjectEditor({
    isEditable,
    customVariables,
    form,
    onFocus: () => {
      setLastFocusedField("subject");
    },
  });

  const { attachments, onUpload, onUploadCancel, onRemoveAttachment } =
    useTemplateEditorAttachments({ form });

  //sync editors template state with isEditable
  useEffect(() => {
    templateMessageEditor.setEditable(isEditable, true);
    templateSubjectEditor.setEditable(isEditable, true);

    if (autoFocus) {
      if (isEditable) {
        (templateType === "INMAIL_TEMPLATE"
          ? templateSubjectEditor
          : templateMessageEditor
        ).commands.focus("end");
      }
    }
  }, [isEditable]);

  const templateType = getValues("type");
  const hasError = "message" in errors || "ai_descriptors" in errors;

  return (
    <div
      id="templateEditor"
      className="flex flex-col flex-1 lg:basis-1/2 gap-1 overflow-hidden p-0.5 -m-0.5"
    >
      <section
        className={clsx(
          "flex flex-1 flex-col overflow-hidden rounded-3xl border bg-white transition-[border] max-lg:max-h-[50dvh] min-h-80",
          hasError
            ? "border-red-500 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-solid has-[:focus-visible]:outline-red-300"
            : clsx(
                "border-black-200",
                getWhitelabelCustomization()?.primary_color
                  ? "has-[:focus-visible]:border-whitelabel-primary"
                  : "has-[:focus-visible]:border-purple-400",
              ),
        )}
      >
        {placeholderText ? (
          <p className="text-black-400">
            Select a template first. The text will show a preview here when
            selected
          </p>
        ) : (
          <>
            <RenderIf condition={templateType === "INMAIL_TEMPLATE"}>
              <TemplateSubjectEditor
                editor={templateSubjectEditor}
                isDisabled={!isEditable}
                errorMessage={"subject" in errors && errors.subject.message}
              />
            </RenderIf>
            <TemplateMessageEditor
              editor={templateMessageEditor}
              templateType={templateType}
              languageDropdownComponent={languageDropdownComponent}
              attachmentsComponent={
                <RenderIf condition={!!attachments.length}>
                  <FileAttachments
                    attachments={attachments}
                    renderAttachment={(attachment) => (
                      <TemplateAttachment
                        attachment={attachment}
                        {...(isEditable && {
                          onRemove: () =>
                            "id" in attachment
                              ? onRemoveAttachment(attachment.id)
                              : onUploadCancel(attachment.file),
                        })}
                      />
                    )}
                  />
                </RenderIf>
              }
            />
            <RenderIf condition={isEditable}>
              <span className="mx-1 lg:mx-2 h-px bg-black-200" />
              <Toolbar
                lastFocusedField={{
                  type: lastFocusedField,
                  editor:
                    lastFocusedField === "message"
                      ? templateMessageEditor
                      : templateSubjectEditor,
                }}
                customVariables={customVariables}
                uploadAttachmentButtonComponent={
                  templateType !== "NOTE_TEMPLATE" && (
                    <UploadAttachmentButton
                      size={isDesktop ? "xs" : "md"}
                      onFileUpload={onUpload}
                      isDisabled={!templateMessageEditor.isEditable}
                      currentFileCount={
                        attachments.filter((attachment) => "id" in attachment)
                          .length
                      }
                    />
                  )
                }
              />
            </RenderIf>
          </>
        )}
      </section>
      {hasError && (
        <p className="ml-1 text-red-500 text-xs">
          {errors.ai_descriptors?.message.message || errors.message?.message}
        </p>
      )}
    </div>
  );
}
